import type { CashflowTarget } from '@/types/api/accounts'
import type { ModelPreview } from '@/types/api/models'
import type {
  SyntheticModelConstraint,
  SyntheticTargetResponse,
} from '@/types/api/synthetic-target'
import type {
  AccountMetadata,
  ModelConstraintValidationWithStatus,
  ProposalBuilderModel,
  ProposalBuilderSleeveState,
} from '@/types/sleeve-management'
import type { ModelBuilderSidePane } from './model-builder'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { createLogger } from '@/lib/logger'
import {
  type DiversificationConstraint,
  ModelConstraintType,
} from '@/types/api/constraints'
import { SleeveCustomizationStatus } from '@/types/enums/sleeve-management'
import { Page } from '@/types/schema/page-configs'
import {
  PROPOSAL_BUILDER_SCHEMA_VERSION,
  type ProposalBuilderConfig,
} from '@/types/schema/page-configs/proposal-builder'

import { defaultPageConfigs } from './defaults/page-configs'

const LOGGER = createLogger('reducers/proposal-builder')

interface LadderAlignedWeights {
  min: number
  max: number
  bondCount: number
}

/**
 * Calculates ladder-aligned diversification weights
 * @param ladderBuckets - Number of buckets/rungs in the ladder policy
 * @param currentMinWeight - Current minimum weight per bond constraint (0-1)
 * @param currentMaxWeight - Current maximum weight per bond constraint (0-1)
 * @returns Ladder-aligned weights with 20% tolerance band
 */
function calculateLadderAlignedWeights(
  ladderBuckets: number,
  currentMinWeight?: number,
  currentMaxWeight?: number,
): LadderAlignedWeights {
  let targetMultiple: number

  if (!currentMinWeight && !currentMaxWeight) {
    // No existing constraint - default to 1 bond per rung
    targetMultiple = 1
  } else {
    // Calculate current implied bond count midpoint
    const minBonds = currentMaxWeight ? Math.ceil(1 / currentMaxWeight) : 1
    const maxBonds = currentMinWeight
      ? Math.floor(1 / currentMinWeight)
      : Infinity

    let midpointBonds: number
    if (maxBonds === Infinity) {
      // Only max weight is set, use a reasonable multiple of min bonds
      midpointBonds = minBonds * 1.5
    } else {
      // Use actual midpoint
      midpointBonds = (minBonds + maxBonds) / 2
    }

    // Find the multiple of ladderBuckets closest to the midpoint
    targetMultiple = Math.round(midpointBonds / ladderBuckets)

    // Ensure we have at least one ladder's worth of bonds
    if (targetMultiple < 1) targetMultiple = 1
  }

  const bondCount = targetMultiple * ladderBuckets

  // Calculate weights with 20% tolerance band (0.8x to 1.2x)
  // Round to 1 decimal place (as percentages)
  const alignedMinWeight = 0.8 * (1 / bondCount)
  const alignedMaxWeight = 1.2 * (1 / bondCount)

  return {
    min: Math.round(alignedMinWeight * 1000) / 1000, // Round to 0.1% (0.001 in decimal)
    max: Math.round(alignedMaxWeight * 1000) / 1000, // Round to 0.1% (0.001 in decimal)
    bondCount,
  }
}

interface ProposalBuilderState {
  config: ProposalBuilderConfig
  configVersion: string
  sleeves: ProposalBuilderSleeveState
  selectedSleeve: string
  // non-null indicates that an existing proposal is being edited
  selectedProposal: string
  selectedAccount: string
  accountMetadata: AccountMetadata
  currentStep: ProposalBuilderSteps
  models: Record<string, ProposalBuilderModel>
  originalSyntheticTarget: SyntheticTargetResponse | null
  activePane: ModelBuilderSidePane | null
  modelPreviewData: ModelPreview | null
  isPreviewLoading: boolean
  isPreviewStale: boolean
  originalPrompt: string | null
  tableView: TableView
}

export enum ProposalBuilderSteps {
  Prompt = 'prompt',
}

enum TableView {
  Universe = 'universe',
  Holdings = 'holdings',
}

// Default values for proposal builder
const DEFAULT_ACCOUNT_NAME = 'Investor'
const DEFAULT_STATE = 'MO'
const DEFAULT_FEDERAL_TAX_RATE = 0.24 // 24%
const DEFAULT_STATE_TAX_RATE = 0 // 0%
const DEFAULT_CASH = 1_000_000

const initialState: ProposalBuilderState = {
  config: defaultPageConfigs[Page.ProposalBuilder],
  configVersion: PROPOSAL_BUILDER_SCHEMA_VERSION,
  originalSyntheticTarget: null,
  currentStep: ProposalBuilderSteps.Prompt,
  sleeves: {},
  selectedSleeve: '',
  selectedProposal: '',
  selectedAccount: '',
  accountMetadata: {
    accountName: DEFAULT_ACCOUNT_NAME,
    taxState: DEFAULT_STATE,
    federalTaxRate: DEFAULT_FEDERAL_TAX_RATE,
    stateTaxRate: DEFAULT_STATE_TAX_RATE,
    cashToInvest: DEFAULT_CASH,
    maxRebalanceSells: null,
    maxRebalanceBuys: null,
    maxRebalanceMarketValuePctSells: null,
  },
  models: {},
  activePane: null,
  modelPreviewData: null,
  isPreviewLoading: false,
  isPreviewStale: true,
  originalPrompt: null,
  tableView: TableView.Universe,
}

const proposalBuilderSlice = createSlice({
  name: 'proposalBuilder',
  initialState,
  reducerPath: 'proposalBuilder',
  reducers: {
    initializeProposalBuilderConfig: (
      state,
      action: PayloadAction<{ config: ProposalBuilderConfig; version: string }>,
    ) => {
      state.config = {
        ...action.payload.config,
        investmentGuidelines:
          action.payload.config.investmentGuidelines ||
          // If the investment guidelines are not set, use the default value
          initialState.config.investmentGuidelines,
      }
      state.configVersion = action.payload.version
    },
    setProposalBuilderConfig: (
      state,
      action: PayloadAction<Partial<ProposalBuilderConfig>>,
    ) => {
      const updatedConfig = {
        ...state.config,
        ...action.payload,
      }
      state.config = updatedConfig
    },
    setOriginalSyntheticTarget: (
      state,
      action: PayloadAction<SyntheticTargetResponse>,
    ) => {
      state.originalSyntheticTarget = action.payload
    },
    setCurrentStep: (state, action: PayloadAction<ProposalBuilderSteps>) => {
      state.currentStep = action.payload
    },
    setSelectedSleeve: (state, action: PayloadAction<string>) => {
      state.selectedSleeve = action.payload
    },
    setSelectedProposal: (state, action: PayloadAction<string>) => {
      state.selectedProposal = action.payload
    },
    setSelectedAccount: (state, action: PayloadAction<string>) => {
      state.selectedAccount = action.payload
    },
    setModels: (
      state,
      action: PayloadAction<Record<string, ProposalBuilderModel>>,
    ) => {
      state.models = action.payload
    },
    setAccountMetadata: (state, action: PayloadAction<AccountMetadata>) => {
      state.accountMetadata = action.payload
    },
    addSleeve: (state, action) => {
      state.sleeves[action.payload.sleeveId] = action.payload.sleeveDetails
    },
    setSleeves: (state, action: PayloadAction<ProposalBuilderSleeveState>) => {
      state.sleeves = action.payload
    },
    addSleeveCustomization: (
      state,
      action: PayloadAction<{
        sleeveId: string
        constraintId: string
        customization: SyntheticModelConstraint
        status?: SleeveCustomizationStatus
        isModelImport?: boolean
      }>,
    ) => {
      const sleeve = state.sleeves[action.payload.sleeveId]
      if (!sleeve) {
        LOGGER.error('Sleeve not found', {
          error: new Error('Sleeve not found'),
          sleeveId: action.payload.sleeveId,
        })
        return
      }

      // Initialize customizations if it doesn't exist
      if (!sleeve.customizations) {
        sleeve.customizations = {}
      }

      // If this is a new diversification constraint (not ISIN/CUSIP), ensure its max weight
      // is at least as high as any existing ISIN/CUSIP constraint
      const customization = action.payload.customization
      let adjustedCustomization: SyntheticModelConstraint = { ...customization }

      if (
        customization.type === ModelConstraintType.Diversification &&
        customization.field !== 'isin' &&
        customization.field !== 'cusip' &&
        customization.max_weight_per_unique_value !== undefined
      ) {
        // Find the highest max weight among ISIN/CUSIP constraints
        let highestIsinCusipMaxWeight = 0
        Object.values(sleeve.customizations || {}).forEach((validation) => {
          const constraint = validation.constraint
          if (
            constraint.type === ModelConstraintType.Diversification &&
            (constraint.field === 'isin' || constraint.field === 'cusip') &&
            constraint.max_weight_per_unique_value !== undefined
          ) {
            highestIsinCusipMaxWeight = Math.max(
              highestIsinCusipMaxWeight,
              constraint.max_weight_per_unique_value,
            )
          }
        })

        // Adjust the new constraint's max weight if necessary
        if (
          customization.max_weight_per_unique_value < highestIsinCusipMaxWeight
        ) {
          // Create a new object with the adjusted max weight
          adjustedCustomization = {
            ...customization,
            max_weight_per_unique_value: highestIsinCusipMaxWeight,
          }
        }
      }

      sleeve.customizations[action.payload.constraintId] = {
        constraint: adjustedCustomization,
        compliant: false,
        status: action.payload.status ?? SleeveCustomizationStatus.New,
        citation: action.payload.customization?.citation,
        // Clear autoAdjustedReason only when manually editing a diversification constraint
        autoAdjustedReason:
          customization.type === ModelConstraintType.Diversification &&
          (customization.field === 'isin' || customization.field === 'cusip')
            ? undefined
            : sleeve.customizations[action.payload.constraintId]
                ?.autoAdjustedReason,
      } as ModelConstraintValidationWithStatus

      // Auto-update max weights for other diversification constraints when ISIN or CUSIP changes
      if (
        customization.type === ModelConstraintType.Diversification &&
        (customization.field === 'isin' || customization.field === 'cusip') &&
        customization.max_weight_per_unique_value !== undefined
      ) {
        // Store the max weight value to avoid TypeScript narrowing issues
        const newMaxWeight = customization.max_weight_per_unique_value

        // Update all other diversification constraints to have at least the same max weight
        Object.entries(sleeve.customizations).forEach(
          ([constraintId, validation]) => {
            const constraint = validation.constraint
            if (
              constraint.type === ModelConstraintType.Diversification &&
              constraint.field !== 'isin' &&
              constraint.field !== 'cusip' &&
              constraintId !== action.payload.constraintId
            ) {
              // Only update if the current max weight is lower than the new ISIN/CUSIP max weight
              if (
                'max_weight_per_unique_value' in constraint &&
                constraint.max_weight_per_unique_value !== undefined &&
                constraint.max_weight_per_unique_value < newMaxWeight
              ) {
                // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
                ;(validation.constraint as any).max_weight_per_unique_value =
                  newMaxWeight
                // Mark as edited if it wasn't already new
                if (validation.status === SleeveCustomizationStatus.Unedited) {
                  validation.status = SleeveCustomizationStatus.Edited
                }
              }
            }
          },
        )
      }

      // Auto-align ISIN/CUSIP constraints when ladder policy changes
      if (
        customization.type === ModelConstraintType.LadderPolicy &&
        customization.start_period !== undefined &&
        customization.end_period !== undefined
      ) {
        const ladderBuckets =
          customization.end_period - customization.start_period

        // Initialize customizations if it doesn't exist
        if (!sleeve.customizations) {
          sleeve.customizations = {}
        }

        // Check if any ISIN/CUSIP constraint exists
        let foundIsinOrCusipConstraint = false

        // Adjust all existing ISIN/CUSIP diversification constraints
        Object.entries(sleeve.customizations).forEach(
          ([_constraintId, validation]) => {
            const constraint = validation.constraint
            if (
              constraint.type === ModelConstraintType.Diversification &&
              (constraint.field === 'isin' || constraint.field === 'cusip')
            ) {
              foundIsinOrCusipConstraint = true

              const minWeight =
                'min_weight_per_unique_value' in constraint
                  ? constraint.min_weight_per_unique_value
                  : undefined
              const maxWeight =
                'max_weight_per_unique_value' in constraint
                  ? constraint.max_weight_per_unique_value
                  : undefined

              const alignedWeights = calculateLadderAlignedWeights(
                ladderBuckets,
                minWeight,
                maxWeight,
              )

              // Update the constraint with aligned weights
              if ('min_weight_per_unique_value' in constraint) {
                // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
                ;(constraint as any).min_weight_per_unique_value =
                  alignedWeights.min
              }
              if ('max_weight_per_unique_value' in constraint) {
                // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
                ;(constraint as any).max_weight_per_unique_value =
                  alignedWeights.max
              }

              // Mark as edited if it wasn't already new
              if (validation.status === SleeveCustomizationStatus.Unedited) {
                validation.status = SleeveCustomizationStatus.Edited
              }

              // Set auto-adjustment reason
              validation.autoAdjustedReason = `Automatically aligned with ${ladderBuckets}-rung ladder policy (${alignedWeights.bondCount} bonds)`
            }
          },
        )

        // If no ISIN or CUSIP constraint exists at all, create a new ISIN constraint
        // If either exists, we've already modified it above
        // Skip creation during model import to avoid creating unnecessary constraints
        if (!foundIsinOrCusipConstraint && !action.payload.isModelImport) {
          const alignedWeights = calculateLadderAlignedWeights(ladderBuckets)
          // Generate ID inline to avoid circular dependency
          const newConstraintId = `customization_diversification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

          const newDiversificationConstraint: DiversificationConstraint = {
            id: newConstraintId,
            type: ModelConstraintType.Diversification,
            field: 'isin',
            min_weight_per_unique_value: alignedWeights.min,
            max_weight_per_unique_value: alignedWeights.max,
          }

          sleeve.customizations[newConstraintId] = {
            constraint:
              newDiversificationConstraint as SyntheticModelConstraint,
            compliant: false,
            status: SleeveCustomizationStatus.New,
            autoAdjustedReason: `Automatically created for ${ladderBuckets}-rung ladder policy (${alignedWeights.bondCount} bonds)`,
          } as ModelConstraintValidationWithStatus
        }

        // After updating ISIN/CUSIP constraints for ladder policy, update other diversification constraints
        // Find the highest max weight among ISIN/CUSIP constraints after ladder adjustments
        let highestIsinCusipMaxWeight = 0
        Object.values(sleeve.customizations || {}).forEach((validation) => {
          const constraint = validation.constraint
          if (
            constraint.type === ModelConstraintType.Diversification &&
            (constraint.field === 'isin' || constraint.field === 'cusip') &&
            constraint.max_weight_per_unique_value !== undefined
          ) {
            highestIsinCusipMaxWeight = Math.max(
              highestIsinCusipMaxWeight,
              constraint.max_weight_per_unique_value,
            )
          }
        })

        // Update all other diversification constraints to have at least the same max weight
        if (highestIsinCusipMaxWeight > 0) {
          Object.entries(sleeve.customizations).forEach(
            ([_constraintId, validation]) => {
              const constraint = validation.constraint
              if (
                constraint.type === ModelConstraintType.Diversification &&
                constraint.field !== 'isin' &&
                constraint.field !== 'cusip'
              ) {
                // Only update if the current max weight is lower than the new ISIN/CUSIP max weight
                if (
                  'max_weight_per_unique_value' in constraint &&
                  constraint.max_weight_per_unique_value !== undefined &&
                  constraint.max_weight_per_unique_value <
                    highestIsinCusipMaxWeight
                ) {
                  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
                  ;(validation.constraint as any).max_weight_per_unique_value =
                    highestIsinCusipMaxWeight
                  // Mark as edited if it wasn't already new
                  if (
                    validation.status === SleeveCustomizationStatus.Unedited
                  ) {
                    validation.status = SleeveCustomizationStatus.Edited
                  }
                  // Set auto-adjustment reason
                  validation.autoAdjustedReason = `Automatically aligned with ISIN/CUSIP constraint set by ladder policy`
                }
              }
            },
          )
        }
      }
    },
    removeSleeveCustomization: (
      state,
      action: PayloadAction<{
        sleeveId: string
        constraintId: string
      }>,
    ) => {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      delete state.sleeves[action.payload.sleeveId]!.customizations[
        action.payload.constraintId
      ]
    },
    setCustomizations: (
      state,
      action: PayloadAction<{
        sleeveId: string
        customizations: Record<string, ModelConstraintValidationWithStatus>
      }>,
    ) => {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      state.sleeves[action.payload.sleeveId]!.customizations =
        action.payload.customizations
    },
    updateSleeve: (state, action) => {
      const { sleeveId, ...sleeveData } = action.payload
      if (sleeveId && state.sleeves[sleeveId]) {
        state.sleeves[sleeveId] = { ...state.sleeves[sleeveId], ...sleeveData }
      }
    },
    removeSleeve: (state, action: PayloadAction<string>) => {
      const sleeveId = action.payload
      const newSleeves = { ...state.sleeves }

      if (newSleeves[sleeveId]) {
        delete newSleeves[sleeveId]
        state.sleeves = newSleeves

        // If the deleted sleeve was the selected one, reset the selected sleeve
        if (state.selectedSleeve === sleeveId) {
          state.selectedSleeve = ''
        }
      }
    },
    updateSleeveOriginalConstraints: (
      state,
      action: PayloadAction<{
        sleeveId: string
        originalConstraints: Record<string, SyntheticModelConstraint>
      }>,
    ) => {
      const { sleeveId, originalConstraints } = action.payload
      if (state.sleeves[sleeveId]) {
        state.sleeves[sleeveId].originalConstraints = originalConstraints
      }
    },
    updateSleeveName: (
      state,
      action: PayloadAction<{ sleeveId: string; name: string }>,
    ) => {
      const { sleeveId, name } = action.payload
      if (state.sleeves[sleeveId]) {
        state.sleeves[sleeveId].sleeveName = name
      }
    },
    updateSleeveCashflowTarget: (
      state,
      action: PayloadAction<{
        sleeveId: string
        cashflowTarget: CashflowTarget[] | null
      }>,
    ) => {
      const { sleeveId, cashflowTarget } = action.payload
      if (state.sleeves[sleeveId]) {
        state.sleeves[sleeveId].cashflowTarget = cashflowTarget
      }
    },
    clearSleeveCashflowTarget: (
      state,
      action: PayloadAction<{ sleeveId: string }>,
    ) => {
      const { sleeveId } = action.payload
      if (state.sleeves[sleeveId]) {
        state.sleeves[sleeveId].cashflowTarget = null
      }
    },
    resetState: () => initialState,
    setActivePane: (state, action) => {
      state.activePane = action.payload
    },
    setPreviewData: (state, action) => {
      state.modelPreviewData = action.payload
    },
    setPreviewLoading: (state, action) => {
      state.isPreviewLoading = action.payload
    },
    setPreviewStale: (state, action: PayloadAction<boolean>) => {
      state.isPreviewStale = action.payload
    },
    setOriginalPrompt: (state, action: PayloadAction<string | null>) => {
      state.originalPrompt = action.payload
    },
    setTableView: (state, action: PayloadAction<TableView>) => {
      state.tableView = action.payload
    },
  },
})

export const {
  initializeProposalBuilderConfig,

  addSleeveCustomization,
} = proposalBuilderSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default proposalBuilderSlice
